import type { AnyResolvedField } from '#layers/data-model/types/schema/resolved-schema';
import { compareSortKeys } from '~/utils/fractional-index';

/**
 * Get the display label for a field, falling back to apiName if no displayName is set
 */
export function getFieldLabel(field: AnyResolvedField): string {
	return field.meta?.displayName ?? field.apiName;
}

/**
 * Sort fields by their sort key (fractional index).
 *
 * Sorting behavior:
 * - Fields with sort keys come first, ordered lexicographically by key
 * - Fields without sort keys come last, ordered alphabetically by label
 */
export function sortFieldsBySortKey<T extends AnyResolvedField>(fields: T[]): T[] {
	return fields.toSorted((a, b) => {
		const aSort = a.meta?.sort ?? null;
		const bSort = b.meta?.sort ?? null;

		// Both have sort keys - compare by byte order (fractional-index contract)
		if (aSort && bSort) {
			return compareSortKeys(aSort, bSort);
		}

		// Only a has sort key - a comes first
		if (aSort) return -1;

		// Only b has sort key - b comes first
		if (bSort) return 1;

		// Neither has sort key - sort alphabetically by label
		return getFieldLabel(a).localeCompare(getFieldLabel(b));
	});
}
